import { useEffect, useRef, useState } from 'react';

// Shared top section for list pages (Invoices, Sales Orders) — styled after
// GiftingDispatch.jsx's dark hero: a dark gradient bar with drifting icons,
// title + greyed italic hint on the left, Download Excel on the right. Below
// it, pages render <CountCard> on the left and their own filters on the right
// (see ListTopRow).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(n) { return String(n).padStart(2, '0'); }

// Local date (not toISOString(), which would shift to yesterday for an IST
// browser before 5:30am).
export function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmt(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
}

// "Today · 24 Oct 2026" / "01 Oct 2026 – 24 Oct 2026" / "From 01 Oct 2026" / "All time"
export function rangeLabel(from, to) {
    if (from && to) {
        if (from === to) return from === todayISO() ? `Today · ${fmt(from)}` : fmt(from);
        return `${fmt(from)} – ${fmt(to)}`;
    }
    if (from) return `From ${fmt(from)}`;
    if (to) return `Up to ${fmt(to)}`;
    return 'All time';
}

const CSS = `
.lh-hero { position: relative; overflow: hidden; border-radius: 20px; padding: 28px 30px;
  background: linear-gradient(135deg, #2A2140 0%, #1A1529 55%, #120D1D 100%);
  border: 1px solid #2A2338; box-shadow: 0 10px 30px rgba(20,16,31,0.3); margin-bottom: 18px; }
.lh-hero-bg { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.lh-float { position: absolute; font-size: 32px; opacity: 0.16; animation: lh-drift 9s ease-in-out infinite;
  filter: drop-shadow(0 4px 10px rgba(0,0,0,0.25)); }
.lh-float-1 { top: 14%; left: 5%;  animation-duration: 8s; }
.lh-float-2 { top: 58%; left: 15%; animation-duration: 10s; animation-delay: 1s; }
.lh-float-3 { top: 18%; left: 86%; animation-duration: 7.5s; animation-delay: .5s; }
.lh-float-4 { top: 64%; left: 76%; animation-duration: 9.5s; animation-delay: 2s; }
.lh-float-5 { top: 36%; left: 46%; animation-duration: 11s; animation-delay: 1.5s; }
.lh-float-6 { top: 12%; left: 62%; animation-duration: 8.5s; animation-delay: .8s; }
.lh-sheen { position: absolute; top: 0; bottom: 0; width: 40%; left: -40%;
  background: linear-gradient(100deg, transparent, rgba(164,143,209,0.10), transparent);
  animation: lh-sheen 7s ease-in-out infinite; }
@keyframes lh-drift { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-16px) rotate(8deg); } }
@keyframes lh-sheen { 0% { left: -40%; } 60%,100% { left: 110%; } }
.lh-content { position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; flex-wrap: wrap; }
.lh-kicker { font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #A48FD1; }
.lh-title { font-size: 30px; font-weight: 600; margin: 6px 0 8px; color: #fff; letter-spacing: -0.01em; }
.lh-hint { font-size: 13.5px; font-style: italic; color: #9A90AE; margin: 0; }
.lh-dl { display: inline-flex; align-items: center; gap: 8px; padding: 12px 20px; border-radius: 10px; border: none;
  background: #5C7E60; color: #fff; font-weight: 700; font-size: 13.5px; cursor: pointer; font-family: inherit;
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease; }
.lh-dl:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(92,126,96,0.45); filter: brightness(1.08); }
.lh-dl:disabled { opacity: .55; cursor: default; }

.lh-row { display: flex; gap: 18px; align-items: stretch; flex-wrap: wrap; margin-bottom: 18px; }
.lh-filters { flex: 1 1 420px; min-width: 0; }

.lh-card { position: relative; flex: 0 0 260px; min-height: 132px; border-radius: 18px; padding: 20px 22px; overflow: hidden;
  color: #fff; cursor: default; isolation: isolate;
  background: linear-gradient(120deg, #6E58A6, #3A6B8A, #8E5FB0, #5C7E60);
  background-size: 300% 300%; animation: lh-grad 12s ease infinite;
  box-shadow: 0 10px 26px rgba(110,88,166,0.35); transition: transform .2s ease, box-shadow .2s ease; }
.lh-card:hover { transform: translateY(-3px) scale(1.015); box-shadow: 0 16px 34px rgba(110,88,166,0.45); }
.lh-card::before, .lh-card::after { content: ''; position: absolute; border-radius: 50%; z-index: -1;
  background: rgba(255,255,255,0.12); animation: lh-bubble 8s ease-in-out infinite; }
.lh-card::before { width: 140px; height: 140px; right: -40px; top: -50px; }
.lh-card::after  { width: 90px; height: 90px; right: 50px; bottom: -45px; animation-delay: 2s; }
.lh-glow { position: absolute; inset: 0; z-index: -1; pointer-events: none; opacity: 0; transition: opacity .2s ease; }
.lh-card:hover .lh-glow { opacity: 1; }
@keyframes lh-grad { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
@keyframes lh-bubble { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-10px,8px) scale(1.12); } }
.lh-card-date { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; letter-spacing: .02em;
  background: rgba(255,255,255,0.16); padding: 5px 11px; border-radius: 999px; }
.lh-pulse { width: 7px; height: 7px; border-radius: 50%; background: #B8F0C0; animation: lh-pulse 1.8s ease-out infinite; }
@keyframes lh-pulse { 0% { box-shadow: 0 0 0 0 rgba(184,240,192,.7); } 100% { box-shadow: 0 0 0 9px rgba(184,240,192,0); } }
.lh-count { font-size: 44px; font-weight: 800; line-height: 1; margin: 14px 0 4px; font-variant-numeric: tabular-nums; }
.lh-count-label { font-size: 12.5px; font-weight: 600; opacity: .85; text-transform: uppercase; letter-spacing: .08em; }

/* Per-row "open PDF" button — the page icon floats gently, lifts on hover. */
.lh-pdf { position: relative; display: inline-grid; place-items: center; width: 36px; height: 36px; border: 0; padding: 0;
  border-radius: 12px; cursor: pointer; background: linear-gradient(135deg, #fde3e1, #f6d0e6);
  box-shadow: 0 3px 8px rgba(208,32,42,0.18); transition: transform .18s ease, box-shadow .18s ease; }
.lh-pdf svg { animation: lh-bob 2.6s ease-in-out infinite; }
.lh-pdf:hover { transform: translateY(-2px) rotate(-4deg) scale(1.08); box-shadow: 0 8px 16px rgba(208,32,42,0.28); }
.lh-pdf:hover svg { animation-duration: .9s; }
.lh-pdf:disabled { cursor: progress; opacity: .6; }
.lh-pdf--loading svg { animation: lh-spin .9s linear infinite; }
@keyframes lh-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
@keyframes lh-spin { to { transform: rotate(360deg); } }
.lh-flush { margin-bottom: 0 !important; }

@media (prefers-reduced-motion: reduce) {
  .lh-float, .lh-sheen, .lh-card, .lh-card::before, .lh-card::after, .lh-pulse, .lh-pdf svg { animation: none !important; }
}
@media (max-width: 640px) {
  .lh-hero { padding: 22px 18px; }
  .lh-title { font-size: 24px; }
  .lh-card { flex: 1 1 100%; }
}
`;

// `flush` drops the bottom margin, for pages whose container already spaces
// its children with a flex gap.
export function ListHero({ kicker, title, hint, icons, onDownload, downloading, downloadTitle, flush }) {
    return (
        <div className={`lh-hero${flush ? ' lh-flush' : ''}`}>
            <style>{CSS}</style>
            <div className="lh-hero-bg">
                <div className="lh-sheen" />
                {icons.slice(0, 6).map((ic, i) => <span key={i} className={`lh-float lh-float-${i + 1}`}>{ic}</span>)}
            </div>
            <div className="lh-content">
                <div>
                    {kicker && <div className="lh-kicker">{kicker}</div>}
                    <h1 className="lh-title">{title}</h1>
                    {hint && <p className="lh-hint">{hint}</p>}
                </div>
                {onDownload && (
                    <button type="button" className="lh-dl" onClick={onDownload} disabled={downloading} title={downloadTitle}>
                        {downloading ? 'Preparing…' : '⬇ Download Excel'}
                    </button>
                )}
            </div>
        </div>
    );
}

// Eases the displayed number toward `value` whenever it changes.
function useCountUp(value) {
    const [shown, setShown] = useState(value);
    const fromRef = useRef(value);
    useEffect(() => {
        const from = fromRef.current;
        if (from === value) return undefined;
        const start = performance.now();
        const dur = 600;
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            const v = Math.round(from + (value - from) * eased);
            setShown(v);
            fromRef.current = v;
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value]);
    return shown;
}

// Interactive animated count card: date range pill, big count, label. The
// radial glow follows the cursor.
export function CountCard({ from, to, count, label, loading }) {
    const shown = useCountUp(count || 0);
    const [glow, setGlow] = useState({ x: 50, y: 50 });
    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setGlow({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
    };
    return (
        <div className="lh-card" onMouseMove={onMove}>
            <div className="lh-glow" style={{ background: `radial-gradient(circle at ${glow.x}% ${glow.y}%, rgba(255,255,255,0.22), transparent 55%)` }} />
            <span className="lh-card-date"><span className="lh-pulse" />{rangeLabel(from, to)}</span>
            <div className="lh-count">{loading ? '…' : shown.toLocaleString('en-IN')}</div>
            <div className="lh-count-label">{label}</div>
        </div>
    );
}

// Animated PDF-page button for a table row.
export function PdfButton({ onClick, loading, title = 'Open PDF in a new tab' }) {
    return (
        <button type="button" className={`lh-pdf${loading ? ' lh-pdf--loading' : ''}`} onClick={onClick} disabled={loading} title={title} aria-label={title}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C0262F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {loading
                    ? <path d="M21 12a9 9 0 1 1-6.2-8.56" />
                    : <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></>}
            </svg>
        </button>
    );
}

// Count card on the left, the page's own filters on the right.
export function ListTopRow({ card, children, flush }) {
    return (
        <div className={`lh-row${flush ? ' lh-flush' : ''}`}>
            {card}
            <div className="lh-filters">{children}</div>
        </div>
    );
}
